import { type BaseEntity } from "@/shared/types";

export interface User extends BaseEntity {
  tenant_id: string;
  email: string;
  firstName: string;
  lastName: string;
  role: string;
  isActive: boolean;
  emailVerified?: boolean;
  warehouseIds?: string[] | null;
  /** TechnicalUser: service queue memberships. */
  queueIds?: string[] | null;
  /** TechnicalUser/Manager: optional default-filter Service Team. */
  primaryQueueId?: string | null;
  /** TechnicalManager: scoped location categories. */
  locationCategoryIds?: string[] | null;
  /** TechnicalManager: scoped individual locations. */
  locationIds?: string[] | null;
  /** Resolved display refs — names for label rendering. */
  queues?: { id: string; name: string }[] | null;
  locationCategories?: { id: string; name: string; color?: string | null }[] | null;
  assignedLocations?: { id: string; name: string }[] | null;
  /** Resolved location access (direct + inherited from queues). Backend-computed. */
  accessibleLocations?: { id: string; name: string }[] | null;
  warehouses?: { id: string; name?: string | null }[] | null;
  account?: {
    id: string;
    name: string;
  };
}

/** Full display name from firstName and lastName */
export function getDisplayName(
  user: { firstName?: string; lastName?: string } | null | undefined
): string {
  if (!user) return "";
  const first = (user.firstName ?? "").trim();
  const last = (user.lastName ?? "").trim();
  return [first, last].filter(Boolean).join(" ") || "";
}

/**
 * Work roles and system roles for ticket access control
 *
 * Roles hierarchy:
 * - BRANCH_USER: Branch employees, limited to their branch
 * - TECHNICIAN: Field technicians, can see all tickets but limited actions
 * - TECH_MANAGER: Technical managers, full ticket control
 * - ACCOUNT_ADMIN: Account administrators, full control + queue/category management
 * - SYSTEM_ADMIN: System support, same as account admin but cross-tenant
 */

// Work roles for ticket system access control
export type WorkRole = "BRANCH_USER" | "TECHNICIAN" | "TECH_MANAGER";

// System-level roles (admin roles that elevate permissions)
export type SystemRole = "SYSTEM_ADMIN" | "ACCOUNT_ADMIN" | "NORMAL_USER";

// Combined effective role for permission checks
export type EffectiveRole =
  | "BRANCH_USER"
  | "TECHNICIAN"
  | "TECH_MANAGER"
  | "ACCOUNT_ADMIN"
  | "SYSTEM_ADMIN";

// Combined user context for permission checks
export interface UserWorkContext {
  id: string;
  account_id: string;
  branch_id?: string; // For BRANCH_USER, their assigned branch
  work_role: WorkRole;
  system_role: SystemRole;
  assigned_queue_ids?: string[]; // Queue IDs the user is assigned to
  /** Preserved backend role when impersonating (e.g. 'LocationUser') so effective role stays correct */
  backendRole?: string;
}

// Permission check results for tickets
export interface TicketPermissions {
  // Read permissions
  canViewTicket: boolean;
  canViewInternalActivities: boolean;

  // Write permissions
  canCreateTicket: boolean;
  canEditTicket: boolean;
  canAssignTicket: boolean; // TECH_MANAGER/ADMIN: can assign anyone (PUSH mode)
  canSelfAssign: boolean; // TECHNICIAN: can self-assign if in queue (PULL mode)
  canChangePriority: boolean;
  canChangeStatus: boolean;
  canCloseTicket: boolean;
  canMarkDone: boolean; // US165: "Erledigt" action for TECHNICIAN, TECH_MANAGER, ADMIN
  canAddPublicComment: boolean;
  canAddInternalNote: boolean;
  canAddAttachment: boolean;
  canChangeQueue: boolean;
  canForwardTicket: boolean;
  canRerouteTicket: boolean;

  // Queue management
  canManageQueues: boolean;
  canManageProblemCategories: boolean;
  canViewDefaultQueue: boolean;
  canDeleteQueue: boolean;

  // Queue membership context (for assignment filtering)
  isQueueMember: boolean; // Is current user member of any linked queue?

  // What statuses can the user set?
  allowedStatuses: string[];
}

export const ROLE_PERMISSIONS: Record<EffectiveRole, Partial<TicketPermissions>> = {
  BRANCH_USER: {
    canViewInternalActivities: false,
    canAssignTicket: false,
    canCloseTicket: false,
    canAddInternalNote: false,
    canChangeQueue: false,
    canChangePriority: false,
    canForwardTicket: false,
    canRerouteTicket: false,
    canManageQueues: false,
    canManageProblemCategories: false,
    canViewDefaultQueue: false,
    canDeleteQueue: false,
    allowedStatuses: ["OPEN"],
  },
  TECHNICIAN: {
    canViewInternalActivities: true,
    canAssignTicket: false,
    canCloseTicket: false,
    canAddInternalNote: true,
    canChangeQueue: false,
    canChangePriority: false,
    canForwardTicket: false,
    canRerouteTicket: false,
    canManageQueues: false,
    canManageProblemCategories: false,
    canViewDefaultQueue: false,
    canDeleteQueue: false,
    allowedStatuses: [
      "OPEN",
      "IN_PROGRESS",
      "SCHEDULED",
      "WAITING_FOR_SPARE_PARTS",
      "QUESTION",
      "RESOLVED",
      "CLOSED",
    ],
  },
  TECH_MANAGER: {
    canViewInternalActivities: true,
    canAssignTicket: true,
    canCloseTicket: true,
    canAddInternalNote: true,
    canChangeQueue: true,
    canChangePriority: true,
    canForwardTicket: true,
    canRerouteTicket: true,
    canManageQueues: false,
    canManageProblemCategories: false,
    canViewDefaultQueue: false,
    canDeleteQueue: false,
    allowedStatuses: [
      "OPEN",
      "IN_PROGRESS",
      "SCHEDULED",
      "WAITING_FOR_SPARE_PARTS",
      "QUESTION",
      "RESOLVED",
      "CLOSED",
    ],
  },
  ACCOUNT_ADMIN: {
    canViewInternalActivities: true,
    canAssignTicket: true,
    canCloseTicket: true,
    canAddInternalNote: true,
    canChangeQueue: true,
    canChangePriority: true,
    canForwardTicket: true,
    canRerouteTicket: true,
    canManageQueues: true,
    canManageProblemCategories: true,
    canViewDefaultQueue: true,
    canDeleteQueue: true,
    allowedStatuses: [
      "OPEN",
      "IN_PROGRESS",
      "SCHEDULED",
      "WAITING_FOR_SPARE_PARTS",
      "QUESTION",
      "RESOLVED",
      "CLOSED",
    ],
  },
  SYSTEM_ADMIN: {
    canViewInternalActivities: true,
    canAssignTicket: true,
    canCloseTicket: true,
    canAddInternalNote: true,
    canChangeQueue: true,
    canChangePriority: true,
    canForwardTicket: true,
    canRerouteTicket: true,
    canManageQueues: true,
    canManageProblemCategories: true,
    canViewDefaultQueue: true,
    canDeleteQueue: true,
    allowedStatuses: [
      "OPEN",
      "IN_PROGRESS",
      "SCHEDULED",
      "WAITING_FOR_SPARE_PARTS",
      "QUESTION",
      "RESOLVED",
      "CLOSED",
    ],
  },
};

/** Used when permissions are enforced by the backend; UI shows all actions. */
export const FULL_TICKET_PERMISSIONS: TicketPermissions = {
  canViewTicket: true,
  canViewInternalActivities: true,
  canCreateTicket: true,
  canEditTicket: true,
  canAssignTicket: true,
  canSelfAssign: true,
  canChangePriority: true,
  canChangeStatus: true,
  canCloseTicket: true,
  canMarkDone: true,
  canAddPublicComment: true,
  canAddInternalNote: true,
  canAddAttachment: true,
  canChangeQueue: true,
  canForwardTicket: true,
  canRerouteTicket: true,
  canManageQueues: true,
  canManageProblemCategories: true,
  canViewDefaultQueue: true,
  canDeleteQueue: true,
  isQueueMember: true,
  allowedStatuses: [
    "OPEN",
    "IN_PROGRESS",
    "SCHEDULED",
    "WAITING_FOR_SPARE_PARTS",
    "QUESTION",
    "RESOLVED",
    "CLOSED",
  ],
};

export const LOCATION_USER_TICKET_PERMISSIONS: TicketPermissions = {
  ...FULL_TICKET_PERMISSIONS,
  canViewInternalActivities: false,
  canAssignTicket: false,
  canSelfAssign: false,
  canChangePriority: false,
  canChangeStatus: false,
  canCloseTicket: false,
  canMarkDone: false,
  canAddInternalNote: false,
  canChangeQueue: false,
  canForwardTicket: false,
  canRerouteTicket: false,
  canManageQueues: false,
  canManageProblemCategories: false,
  canViewDefaultQueue: false,
  canDeleteQueue: false,
  isQueueMember: false,
  allowedStatuses: [],
};

// Legacy alias for backward compatibility
export const WORK_ROLE_PERMISSIONS: Record<WorkRole | "ELEVATED", Partial<TicketPermissions>> = {
  BRANCH_USER: ROLE_PERMISSIONS.BRANCH_USER,
  TECHNICIAN: ROLE_PERMISSIONS.TECHNICIAN,
  TECH_MANAGER: ROLE_PERMISSIONS.TECH_MANAGER,
  ELEVATED: ROLE_PERMISSIONS.ACCOUNT_ADMIN, // Backward compat
};

/**
 * Determine the effective role based on work_role and system_role
 * System roles (ACCOUNT_ADMIN, SYSTEM_ADMIN) override work_role
 */
export function getEffectiveRole(workRole: WorkRole, systemRole: SystemRole): EffectiveRole {
  // System admins and account admins always have their elevated role
  if (systemRole === "SYSTEM_ADMIN") return "SYSTEM_ADMIN";
  if (systemRole === "ACCOUNT_ADMIN") return "ACCOUNT_ADMIN";

  // Otherwise use the work role
  return workRole;
}

/**
 * Check if a role can view all tickets in the account (not branch-restricted)
 */
export function canViewAllBranches(effectiveRole: EffectiveRole): boolean {
  return effectiveRole !== "BRANCH_USER";
}

/**
 * Check if a role can view internal activities
 */
export function canAccessInternalNotes(effectiveRole: EffectiveRole): boolean {
  return effectiveRole !== "BRANCH_USER";
}
